import React, { useState, useRef, useEffect } from 'react';
import { Button, Badge } from '@heroui/react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationDropdown = () => {
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications
  } = useNotifications();

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);

  console.log('NotificationDropdown: Rendered with:', {
    notificationsCount: notifications.length,
    unreadCount,
    isLoading,
    currentPage,
    hasMore
  });

  const handleNotificationClick = (notification) => {
    console.log('NotificationDropdown: Clicked notification:', notification);
    const postId = notification.post_id || notification.post?._id?.['$oid'] || notification.post?._id;
    console.log('NotificationDropdown: Extracted postId:', postId);
    if (postId) {
      console.log('NotificationDropdown: Navigating to:', `/posts/${postId}`);
      navigate(`/posts/${postId}`);
      setIsOpen(false);
    } else {
      console.log('NotificationDropdown: No postId found, notification structure:', JSON.stringify(notification, null, 2));
    }
  };

  const loadMoreNotifications = async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    try {
      const result = await fetchNotifications(currentPage + 1, 10);
      setCurrentPage(prev => prev + 1);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !isLoadingMore) {
      loadMoreNotifications();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Reset pagination when opening dropdown
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHasMore(true);
    }
  }, [isOpen]);

  const handleToggle = () => {
    console.log('NotificationDropdown: Toggle clicked, current isOpen:', isOpen);

    if (!isOpen && notifications.length === 0 && !isLoading) {
      console.log('NotificationDropdown: No notifications, fetching...');
      fetchNotifications();
    }

    setIsOpen(!isOpen);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Badge
        content={unreadCount > 0 ? unreadCount : null}
        color="danger"
        size="sm"
        isInvisible={unreadCount === 0}
      >
        <Button
          isIconOnly
          variant="light"
          className="text-gray-600 hover:text-gray-900"
          onPress={handleToggle}
        >
          <Bell className="w-5 h-5" />
        </Button>
      </Badge>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => setIsOpen(false)}
              className="text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Loading State */}
          {isLoading && notifications.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-500">Loading notifications...</span>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && notifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600 font-medium">No notifications yet</p>
              <p className="text-xs mt-1 text-gray-400">You'll see activity notifications here</p>
            </div>
          )}

          {/* Notifications List */}
          {notifications.length > 0 && (
            <div 
              ref={scrollRef}
              className="max-h-80 overflow-y-auto"
              onScroll={handleScroll}
            >
              {notifications.map((notification, index) => (
                <div
                  key={notification.id || index}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors p-4 border-b border-gray-100 ${
                    !notification.seen ? 'bg-blue-50 border-l-4 border-l-blue-400' : 'bg-white'
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 p-2 rounded-full ${
                      !notification.seen ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Bell className="w-4 h-4" />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        !notification.seen ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {notification.title || 'Notification'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {notification.content || notification.body || 'No content'}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          {formatTimestamp(notification.timestamp || notification.created_at)}
                        </span>
                        {!notification.seen && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-xs text-gray-500">Loading more...</span>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
              <div className="text-center text-xs text-gray-400">
                Showing {notifications.length} notifications{hasMore ? ' - scroll for more' : ''}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
